import React from 'react';
import { motion } from 'framer-motion';
import { Home, CheckSquare, Users, BarChart3, Plus, LayoutGrid, FileText, Shield, UserCog, UserPlus, Contact, Workflow } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  // Generate initials from user name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  const menuItems: Array<{ icon: React.ComponentType<any>; label: string; path: string; count?: number; section?: string }> = [
    { icon: Home, label: 'Dashboard', path: '/' },
    // { icon: CheckSquare, label: 'All Tasks', path: '/tasks', count: 12 },
    { icon: Plus, label: 'Project Intake', path: '/intake' },
     { icon: FileText, label: 'Dynamic Form', path: '/form' },
    { icon: CheckSquare, label: 'Project Requests', path: '/project-requests' },
    { icon: BarChart3, label: 'Deals', path: '/deals' },
    { icon: Users, label: 'ClientTRAC', path: '/customers' },
    { icon: Contact, label: 'Customer Contacts', path: '/customer-contacts' },
    { icon: BarChart3, label: 'JobTRAC', path: '/projects' },
    { icon: FileText, label: 'Project Cases', path: '/cases' },
    { icon: LayoutGrid, label: 'Project Board', path: '/projects/board' },
    { icon: Workflow, label: 'Form Feeds', path: '/form-feeds', section: 'feeds' },
    // Administration items (conditionally shown)
    ...(hasPermission('users.view') ? [
      { icon: UserCog, label: 'User Management', path: '/users' },
      { icon: Shield, label: 'Roles & Permissions', path: '/roles' },
    ] : []),
    ...(hasPermission('teams.view') ? [
      { icon: UserPlus, label: 'Teams', path: '/teams' },
    ] : []),
    // Removed: In Progress, Calendar, Analytics, Favorites, Archive
  ];

  // Removed sample projects list

  return (
    <motion.aside
      initial={{ x: -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="relative w-64 theme-sidebar border-r theme-border flex flex-col h-screen overflow-hidden"
    >
      {/* Content */}
      <div className="relative z-10 flex flex-col h-full">
        <div className="px-4 py-4 border-b theme-border">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }} className="flex items-center gap-3">
            <div className="relative">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg p-1.5">
                <img
                  src="/logo.png"
                  alt="SelectRow Logo"
                  className="w-full h-full object-contain relative z-10"
                  style={{
                    filter: 'brightness(0) invert(1) drop-shadow(0 0 4px rgba(255,255,255,0.3))'
                  }}
                />
              </div>
            </div>
            <div>
              <h2 className="text-sm font-semibold theme-text-primary leading-tight">SelectRow</h2>
              <p className="text-xs theme-text-tertiary">Project Management</p>
            </div>
          </motion.div>
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          <div className="space-y-0.5">
            {menuItems.map((item, index) => {
              let isActive = false;
              if (item.path) {
                if (item.path === '/projects') {
                  isActive = location.pathname === '/projects' || location.pathname === '/projects/';
                } else if (item.path === '/form-feeds') {
                  isActive = location.pathname.startsWith('/form-feeds');
                } else {
                  isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                }
              }
              
              // Add section divider before Form Feeds
              const showDivider = item.section === 'feeds' && index > 0;
              
              return (
                <React.Fragment key={item.label}>
                  {showDivider && (
                    <div className="py-2">
                      <div className="border-t theme-border"></div>
                      <p className="text-xs font-semibold theme-text-tertiary uppercase tracking-wider mt-3 mb-1 px-3">
                        Management
                      </p>
                    </div>
                  )}
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }}>
                    <motion.button
                      whileHover={{ x: 2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => item.path && navigate(item.path)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-all duration-150 text-sm
                        ${isActive
                          ? 'bg-[#0073ea] text-white'
                          : 'theme-text-secondary hover:theme-bg-tertiary hover:theme-text-primary'}
                      `}
                    >
                      <div className="flex items-center gap-2.5">
                        <item.icon className={`w-4 h-4 ${isActive ? 'text-white' : 'theme-text-tertiary'}`} />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {item.count && (
                        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: index * 0.03 + 0.1 }} className={`px-1.5 py-0.5 text-xs rounded-full ${isActive ? 'bg-white/20 text-white' : 'theme-bg-tertiary theme-text-secondary'}`}>
                          {item.count}
                        </motion.span>
                      )}
                    </motion.button>
                  </motion.div>
                </React.Fragment>
              );
            })}
          </div>
        </nav>

        <div className="p-3 border-t theme-border">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex items-center gap-2.5 p-2.5 rounded-lg theme-bg-tertiary hover:opacity-80 transition-all duration-150 cursor-pointer">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-semibold text-xs">
                {user ? getInitials(user.name) : '?'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium theme-text-primary text-sm truncate">
                {user?.name || 'Guest'}
              </p>
              <p className="text-xs theme-text-tertiary truncate">
                {user?.email || 'Not logged in'}
              </p>
            </div>
            <motion.div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }} />
          </motion.div>
        </div>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
